import { forwardRef, SystemStyleObject, useColorModeValue } from '@chakra-ui/react';
import { ChakraStylesConfig, CreatableSelect, Select as RSelect } from 'chakra-react-select';
import { useState } from 'react';

import { ReactSelectProps } from '../utils/types';

export const ReactSelect = forwardRef(
  (
    {
      value: controlledValue,
      onChange: controlledOnChange,
      name,
      max,
      options,
      defaultValue,
      type,
      showDropdownIndicator = true,
      isMulti = false,
      isPortaled = false,
      isHighlighted = false,
      isOutlined = false,
      ...rest
    }: ReactSelectProps & { isHighlighted?: boolean },
    ref,
  ) => {
    const [value, setValue] = useState(defaultValue);

    const defaultOption = defaultValue ? { value: defaultValue, label: defaultValue } : '';
    const bgColor = useColorModeValue(
      isHighlighted ? 'orange.200' : 'gray.100',
      isHighlighted ? 'orange.800' : 'whiteAlpha.50',
    );
    const bgColorHover = useColorModeValue(
      isHighlighted ? 'orange.300' : 'gray.100',
      isHighlighted ? 'orange.700' : 'whiteAlpha.100',
    );
    const tagVariant = useColorModeValue('solid', 'subtle');

    const chakraStyles: ChakraStylesConfig = {
      control: (provided: SystemStyleObject) => ({
        ...provided,
        border: 0,
      }),
      dropdownIndicator: (provided: SystemStyleObject) => ({
        ...provided,
        bgColor,
        px: 2,
        _hover: { bgColor: bgColorHover },
        display: showDropdownIndicator ? 'flex' : 'none',
      }),
      indicatorSeparator: () => ({ display: 'none' }),
      valueContainer: (provided: SystemStyleObject, { hasValue, isMulti }) => ({
        ...provided,
        bgColor,
        px: hasValue && isMulti ? 2 : 4,
        py: 2,
        _hover: { bgColor: bgColorHover },
      }),
      option: (provided: SystemStyleObject) => ({
        ...provided,
        pl: 4,
      }),
      group: () => ({ pb: 4, pl: 4 }),
      groupHeading: (provided: SystemStyleObject) => ({
        ...provided,
        p: 4,
        ml: -4,
        fontSize: 16,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        borderWidth: 0,
      }),
      menuList: (provided: SystemStyleObject) => ({
        ...provided,
        width: '200px',
      }),
      multiValue: (provided) => ({
        ...provided,
        fontFamily: '"Fira Code", monospace',
      }),
    };

    // Bordered like an outline `Input`, readable chips, and a full-width menu that floats above
    // drawers instead of being clipped by their scroll area
    const outlinedStyles: ChakraStylesConfig = {
      control: (provided: SystemStyleObject, { isFocused }) => ({
        ...provided,
        minH: 10,
        bg: 'bg.surface',
        borderRadius: 'md',
        borderColor: isFocused ? 'accent' : 'chakra-border-color',
        boxShadow: isFocused ? '0 0 0 1px var(--chakra-colors-accent)' : 'none',
        _hover: { borderColor: 'accent' },
      }),
      valueContainer: (provided: SystemStyleObject) => ({ ...provided, px: 3, py: 1, gap: 1.5 }),
      placeholder: (provided: SystemStyleObject) => ({ ...provided, color: 'placeholder' }),
      indicatorSeparator: () => ({ display: 'none' }),
      dropdownIndicator: (provided: SystemStyleObject) => ({
        ...provided,
        bg: 'transparent',
        px: 3,
        color: 'fg.muted',
        display: showDropdownIndicator ? 'flex' : 'none',
      }),
      multiValue: (provided: SystemStyleObject) => ({
        ...provided,
        bg: 'bg.muted',
        color: 'fg.default',
        borderRadius: 'base',
        fontWeight: 'medium',
      }),
      menuList: (provided: SystemStyleObject) => ({ ...provided, p: 1, borderRadius: 'md', shadow: 'lg' }),
      option: (provided: SystemStyleObject, { isFocused }) => ({
        ...provided,
        px: 3,
        py: 2.5,
        borderRadius: 'base',
        bg: isFocused ? 'bg.muted' : 'transparent',
        color: 'fg.default',
      }),
    };
    const isMenuPortaled = isPortaled || isOutlined;
    // Above Chakra's modal layer (1400), or a portaled menu opens behind the drawer
    const portalStyles = isMenuPortaled ? { menuPortal: (base: any) => ({ ...base, zIndex: 1500 }) } : undefined;

    const finalValue: any = controlledValue || value || '';

    const finalSetValue = controlledOnChange || setValue;

    return (
      <>
        {type === 'creatable' ? (
          <CreatableSelect
            chakraStyles={isOutlined ? outlinedStyles : chakraStyles}
            tagVariant={isOutlined ? 'subtle' : tagVariant}
            styles={portalStyles}
            size="sm"
            isClearable={false}
            isMulti={isMulti}
            name={name}
            onChange={finalSetValue}
            value={finalValue}
            options={options}
            defaultValue={defaultOption}
            menuPortalTarget={isMenuPortaled ? document.body : undefined}
            {...rest}
            ref={ref}
          />
        ) : (
          <RSelect
            chakraStyles={isOutlined ? outlinedStyles : chakraStyles}
            tagVariant={isOutlined ? 'subtle' : tagVariant}
            styles={portalStyles}
            size="sm"
            isClearable={false}
            isMulti={isMulti}
            onChange={finalSetValue}
            value={finalValue}
            options={options}
            isOptionDisabled={() => !!max && finalValue?.length >= max}
            defaultValue={defaultOption}
            menuPortalTarget={isMenuPortaled ? document.body : undefined}
            menuPlacement="auto"
            {...rest}
            ref={ref}
          />
        )}
      </>
    );
  },
);
